import i18n from "i18next"
import {initReactI18next} from "react-i18next";

import {applyDocumentLanguage, applyFontScale, getFontScale} from '@plannel/utils/font-util'

import translationEnUs from '@plannel/assets/data/l10n/translation.en-us'
import translationKoKr from '@plannel/assets/data/l10n/translation.ko-kr'
import translationJaJp from '@plannel/assets/data/l10n/translation.ja-jp'
import translationZhTw from '@plannel/assets/data/l10n/translation.zh-tw'
import translationZhCn from '@plannel/assets/data/l10n/translation.zh-cn'
import translationViVn from '@plannel/assets/data/l10n/translation.vi-vn'

const resource = {
    "en-US": {
        translation: {
            ...translationEnUs.menu,
            ...translationEnUs.msg,
            ...translationEnUs.problem,
            ...translationEnUs.notification,
            ...translationEnUs.plannelAgent,
            ...translationEnUs.fp
        },
        grid: translationEnUs.grid
    },
    "ko-KR": {
        translation: {
            ...translationKoKr.menu,
            ...translationKoKr.msg,
            ...translationKoKr.problem,
            ...translationKoKr.notification,
            ...translationKoKr.plannelAgent,
            ...translationKoKr.fp
        },
        grid: translationKoKr.grid
    },
    "ja-JP": {
        translation: {
            ...translationJaJp.menu,
            ...translationJaJp.msg,
            ...translationJaJp.problem,
            ...translationJaJp.notification,
            ...translationJaJp.plannelAgent,
            ...translationJaJp.fp
        },
        grid: translationJaJp.grid
    },
    "zh-TW": {
        translation: {
            ...translationZhTw.menu,
            ...translationZhTw.msg,
            ...translationZhTw.problem,
            ...translationZhTw.notification,
            ...translationZhTw.plannelAgent,
            ...translationZhTw.fp
        },
        grid: translationZhTw.grid
    },
    "zh-CN": {
        translation: {
            ...translationZhCn.menu,
            ...translationZhCn.msg,
            ...translationZhCn.problem,
            ...translationZhCn.notification,
            ...translationZhCn.plannelAgent,
            ...translationZhCn.fp
        },
        grid: translationZhCn.grid
    },
    "vi-VN": {
        translation: {
            ...translationViVn.menu,
            ...translationViVn.msg,
            ...translationViVn.problem,
            ...translationViVn.notification,
            ...translationViVn.plannelAgent,
            ...translationViVn.fp
        },
        grid: translationViVn.grid
    }
};

export const languages = {
    EN_US: "en-US",
    KO_KR: "ko-KR",
    JA_JP: "ja-JP",
    ZH_TW: "zh-TW",
    ZH_CN: "zh-CN",
    VI_VN: "vi-VN",
}

/**
 * i18next 언어 코드 → saas-ai-agent language 파라미터 변환
 * 백엔드 _LANG_INSTRUCTION 키와 일치: ko / en / ja / zh-TW / zh-CN / vi
 *
 * @param {string} i18nLang - i18next.language (예: "ko-KR", "en-US", "ja-JP")
 * @returns {string} - 단축 언어 코드 (예: "ko", "en", "ja", "zh-TW")
 */
export const toAgentLanguage = (i18nLang) => {
    const code = (i18nLang || "ko-KR").toLowerCase();
    if (code.startsWith("ko")) return "ko";
    if (code.startsWith("en")) return "en";
    if (code.startsWith("ja")) return "ja";
    if (code.startsWith("zh-tw")) return "zh-TW";
    if (code.startsWith("zh-cn") || code.startsWith("zh")) return "zh-CN";
    if (code.startsWith("vi")) return "vi";
    return "en"; // fallback
};

/**
 * 현재 앱에서 선택된 언어를 saas-ai-agent language 파라미터로 반환.
 * 비-컴포넌트(서비스 모듈)에서 동기적으로 현재 언어를 읽을 때 사용.
 */
export const getAgentLanguage = () => toAgentLanguage(i18n.language);

export const getLanguageByBrowser = () => {
    const browserLangCode = navigator.language;

    let languageCode = languages.EN_US;
    switch (browserLangCode) {
        case "ko":
        case languages.KO_KR:
            languageCode = languages.KO_KR;
            break;
        case "ja":
        case languages.JA_JP:
            languageCode = languages.JA_JP;
            break;
        case languages.ZH_TW:
            languageCode = languages.ZH_TW;
            break;
        case languages.ZH_CN:
            languageCode = languages.ZH_CN;
            break;
        case languages.VI_VN:
            languageCode = languages.VI_VN;
            break;
        default:
            break;
    }

    const settingsData = { lang: languageCode };
    localStorage.setItem("settings", JSON.stringify(settingsData));

    return languageCode;
}

const getLocalLang = () => {
    const settingsData = JSON.parse(localStorage.getItem("settings"));
    if (settingsData?.lang) {
        return settingsData.lang;
    }

    return getLanguageByBrowser();
}

i18n.use(initReactI18next)  // passes i18n down to react-i18next
    .init({
        resources: resource,
        lng: getLocalLang(),
        fallbackLng: languages.EN_US,
        debug: true,
        keySeparator: false, // we do not use keys in form messages.welcome
        interpolation: {
            escapeValue: false // react already safes from xss
        }
    });

// <html lang> + 폰트 CSS 변수를 언어에 맞춘다. 언어 전환 진입점이 여러 곳(Navbar,
// SelectLanguage)이라 각 호출부 대신 i18next 이벤트 한 곳에서 처리한다.
i18n.on("languageChanged", applyDocumentLanguage);
applyDocumentLanguage(i18n.language);

// 폰트 크기 스케일(Big/Small)을 저장값으로 복원한다. 폰트 관련 문서 초기화가 여기 모여
// 있어 같이 둔다 — 언어와 달리 이벤트가 없고 앱 시작 시 1회면 된다.
applyFontScale(getFontScale());

/*
 * 언어 전환 단일 진입점(Navbar, SelectLanguage 공용).
 * localStorage 를 **먼저** 갱신한 뒤 changeLanguage 를 호출한다. 순서가 뒤집히면
 * languageChanged 리스너가 localStorage 에서 이전 언어를 읽는다.
 */
export const changeAppLanguage = (lang) => {
    let settingsData = {};
    try {
        settingsData = JSON.parse(localStorage.getItem("settings")) ?? {};
    } catch (e) {
        settingsData = {};
    }

    settingsData.lang = lang;
    localStorage.setItem("settings", JSON.stringify(settingsData));

    i18n.changeLanguage(lang);
};

export default i18n;
